import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney, fmtNum, fmtTime, tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusPill, LoadingBlock, EmptyState } from "@/components/common";
import { EventDialog, FieldDef } from "@/components/mc/EventDialog";
import { useOnline, pendingForDay, syncPending, removePending, PendingCapture } from "@/lib/connectivity";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Radio, AlertTriangle, Wrench, PackageX, MessageSquarePlus, Gauge, RefreshCw,
  DoorClosed, PlayCircle, ListChecks, BarChart3, TrendingUp, TrendingDown, Minus,
  History, CircleDashed, Target,
} from "lucide-react";
import { toast } from "sonner";

import { useI18n } from "@/context/I18nContext";

const SEV: Record<string, string> = { low: "info", medium: "warn", high: "warn", critical: "critical" };

const Section: React.FC<{ title: string; icon: React.ReactNode; count?: number; children: React.ReactNode }> = ({ title, icon, count, children }) => (
  <Card className="p-4">
    <div className="mb-3 flex items-center gap-2 font-display text-sm font-semibold">
      {icon} {title} {count !== undefined && <span className="text-muted-foreground">({count})</span>}
    </div>
    {children}
  </Card>
);

export default function StoreDay({ storeIdProp }: { storeIdProp?: string }) {
  const {
    t
  } = useI18n();

  const params = useParams();
  const storeId = storeIdProp || params.storeId!;
  const { hasPerm } = useAuth();
  const canRun = hasPerm("mc.run");

  const [loading, setLoading] = useState(true);
  const [store, setStore] = useState<any>(null);
  const [day, setDay] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [dialog, setDialog] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingCapture[]>([]);
  const online = useOnline();

  const loadDetail = useCallback(async (dayId: string) => {
    const r = await api.get(`/mc/store-days/${dayId}`);
    setDetail(r.data);
  }, []);

  const load = useCallback(async () => {
    try {
      const [storeRes, activeRes, histRes] = await Promise.all([
        api.get(`/org/stores/${storeId}`),
        api.get(`/mc/store-days/active?store_id=${storeId}`),
        api.get(`/mc/store-days?store_id=${storeId}&limit=20`),
      ]);
      setStore(storeRes.data.store);
      setDay(activeRes.data.day);
      setHistory(histRes.data.days);
      if (activeRes.data.day) await loadDetail(activeRes.data.day.id);
      else setDetail(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Failed to load store day");
    } finally {
      setLoading(false);
    }
  }, [storeId, loadDetail]);

  useEffect(() => { load(); }, [load]);

  const dayId0 = day?.id;
  const refreshPending = useCallback(() => { if (dayId0) setPending(pendingForDay(dayId0)); }, [dayId0]);
  useEffect(() => { refreshPending(); }, [refreshPending, detail]);

  // Auto-sync queued captures when connectivity returns (idempotent server-side)
  useEffect(() => {
    if (online && dayId0 && pendingForDay(dayId0).length > 0) {
      syncPending().then((n) => {
        if (n > 0) { toast.success(tl("{n} offline capture(s) synced", { n })); if (dayId0) loadDetail(dayId0); }
        refreshPending();
      });
    }
  }, [online, dayId0, loadDetail, refreshPending]);

  const openDay = async (mod: string, priorities: string) => {
    try {
      await api.post("/mc/store-days/open", {
        store_id: storeId, manager_on_duty: mod,
        priorities: priorities.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Operating day opened");
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not open day");
    }
  };

  if (loading) return <LoadingBlock />;

  const linked = detail?.linked || {};
  const dayId = day?.id;

  return (
    (<div>
      <PageHeader
        title="Shift Command"
        subtitle={store ? `${store.name} · #${store.store_number}` : ""}
        testId="shift-command-header"
        actions={day && <StatusPill status={day.status === "active" ? "active" : "info"} testId="day-status">{day.status === "active" ? t("Day open") : t("Day closed")}</StatusPill>}
      />
      <Tabs defaultValue="today">
        <TabsList className="mb-4">
          <TabsTrigger value="today" data-testid="tab-today">{t("Today")}</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">{t("History")}</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">{t("Reviews")}</TabsTrigger>
        </TabsList>

        <TabsContent value="today">
          {!day ? (
            <OpenDayPanel canRun={canRun} onOpen={openDay} />
          ) : (
            <div className="space-y-4">
              {pending.length > 0 && (
                <Card className="border-status-warn/40 bg-status-warn/5 p-4" data-testid="unsynced-captures">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-status-warn">{pending.length}{" "}{t("unsynced capture(s) on this device")}</div>
                    <Button size="sm" variant="outline" data-testid="sync-now"
                      onClick={async () => { const n = await syncPending(); if (n > 0 && dayId) loadDetail(dayId); refreshPending(); toast.success(tl("{n} synced", { n })); }}>{t("Sync now")}</Button>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {pending.map((p) => (
                      <div key={p.client_id} className="flex items-center justify-between rounded border border-status-warn/30 bg-card px-2.5 py-1.5 text-sm">
                        <span className="truncate">{p.label}</span>
                        <span className="ml-2 rounded-full bg-status-warn/15 px-2 py-0.5 text-[10px] font-medium uppercase text-status-warn">{t("unsynced")}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
              {/* Opening summary */}
              <Section title={t("Opening Control")} icon={<PlayCircle size={16} className="text-brand" />}>
                <div className="grid gap-3 text-sm sm:grid-cols-2">
                  <Field label="Manager on duty" value={day.manager_on_duty} />
                  <Field label="Business date" value={day.business_date} />
                  <Field label="Readiness" value={day.opening?.readiness} />
                  <Field label="Staffing gaps" value={day.opening?.staffing_gaps || "None noted"} />
                </div>
                {day.opening?.priorities?.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs font-semibold uppercase text-muted-foreground">{t("Day priorities")}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {day.opening.priorities.map((p: string, i: number) => (
                        <span key={i} className="rounded bg-accent px-2 py-0.5 text-xs text-accent-foreground">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </Section>

              {/* Today's Performance — read-only KPI summary (below Opening Control, above
                  Coverage). Gated on intel.view so an unauthorized associate fires NO request. */}
              {hasPerm("intel.view") && <PerformancePanel storeId={storeId} />}

              {/* Checkpoint B — Actual vs Forecast vs Goal (read-only). Gated on goal.view so an
                  unauthorized associate fires NO request. */}
              {hasPerm("goal.view") && <GoalForecastPanel storeId={storeId} />}

              {/* Read-only planned-vs-actual coverage context (authoritative source: Workforce) */}
              <CoveragePanel storeId={storeId} />
              <AttendancePanel storeId={storeId} />

              {/* Live capture actions */}
              {day.status === "active" && canRun && (
                <div className="flex flex-wrap gap-2" data-testid="live-actions">
                  <Button variant="outline" size="sm" onClick={() => setDialog("capture")} data-testid="btn-capture"><MessageSquarePlus size={15} className="mr-1.5" />{t("Quick Capture")}</Button>
                  <Button variant="outline" size="sm" onClick={() => setDialog("condition")} data-testid="btn-condition"><AlertTriangle size={15} className="mr-1.5" />{t("Condition")}</Button>
                  <Button variant="outline" size="sm" onClick={() => setDialog("intervention")} data-testid="btn-intervention"><Wrench size={15} className="mr-1.5" />{t("Intervention")}</Button>
                  <Button variant="outline" size="sm" onClick={() => setDialog("missed")} data-testid="btn-missed"><PackageX size={15} className="mr-1.5" />{t("Missed Demand")}</Button>
                  <Button variant="outline" size="sm" onClick={() => setDialog("kpi")} data-testid="btn-kpi"><Gauge size={15} className="mr-1.5" />{t("KPI Check")}</Button>
                  <Button variant="outline" size="sm" onClick={() => setDialog("midshift")} data-testid="btn-midshift"><RefreshCw size={15} className="mr-1.5" />{t("Midshift Reset")}</Button>
                  <Button size="sm" onClick={() => setDialog("close")} data-testid="btn-close"><DoorClosed size={15} className="mr-1.5" />{t("Closing Handoff")}</Button>
                </div>
              )}

              <div className="grid gap-4 lg:grid-cols-2">
                <Section title={t("Operating Conditions")} icon={<AlertTriangle size={16} className="text-status-warn" />} count={linked.condition?.length}>
                  <ItemList items={linked.condition} render={(c: any) => (
                    <div className="flex items-start justify-between gap-2">
                      <div><div className="font-medium">{c.title}</div>{c.business_impact && <div className="text-xs text-muted-foreground">{c.business_impact}</div>}</div>
                      <StatusPill status={SEV[c.severity] || "info"}>{stat(c.severity)}</StatusPill>
                    </div>
                  )} empty="No conditions captured" />
                </Section>
                <Section title={t("Interventions")} icon={<Wrench size={16} className="text-status-info" />} count={linked.intervention?.length}>
                  <ItemList items={linked.intervention} render={(i: any) => (
                    <div><div className="font-medium">{i.action_taken}</div>{i.rationale && <div className="text-xs text-muted-foreground">{i.rationale}</div>}
                      {i.condition_ids?.length > 0 && <div className="mt-0.5 text-[11px] text-brand">{t("linked to")}{" "}{i.condition_ids.length}{" "}{t("condition(s)")}</div>}</div>
                  )} empty="No interventions recorded" />
                </Section>
                <Section title={t("Missed Demand")} icon={<PackageX size={16} className="text-status-critical" />} count={linked.missed_demand?.length}>
                  <ItemList items={linked.missed_demand} render={(m: any) => (
                    <div className="flex items-start justify-between gap-2">
                      <div><div className="font-medium">{m.reason}</div>{m.description && <div className="text-xs text-muted-foreground">{m.description}</div>}</div>
                      {m.estimated_units != null && <span className="text-xs text-muted-foreground">~{m.estimated_units}{" "}{t("units")}</span>}
                    </div>
                  )} empty="No missed demand reported" />
                </Section>
                <Section title={t("KPI Checks")} icon={<Gauge size={16} className="text-brand" />} count={day.kpi_checks?.length}>
                  <ItemList items={day.kpi_checks} render={(k: any) => (
                    <div className="flex items-start justify-between gap-2">
                      <div><div className="font-medium">{k.metric}</div>{k.context_note && <div className="text-xs text-muted-foreground">{k.context_note}</div>}</div>
                      <div className="text-right text-xs">
                        <div>{k.actual ?? "—"} / {k.target ?? "—"}</div>
                        {k.variance != null && <div className={k.variance < 0 ? t("text-status-critical") : t("text-status-good")}>{k.variance > 0 ? "+" : ""}{k.variance}</div>}
                      </div>
                    </div>
                  )} empty="No KPI checks yet" />
                </Section>
              </div>

              <div data-testid="section-captures">
              <Section title={t("Quick Captures")} icon={<MessageSquarePlus size={16} className="text-brand" />} count={linked.capture?.length}>
                <ItemList items={linked.capture} render={(c: any) => (
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium">{c.note}</div>
                    <span className="text-xs capitalize text-muted-foreground">{stat(c.category)}</span>
                  </div>
                )} empty="No quick captures yet" />
              </Section>
              </div>

              {day.midshift && (
                <Section title={t("Midshift Reset")} icon={<RefreshCw size={16} className="text-status-info" />}>
                  <div className="text-sm">{day.midshift.plan_vs_reality}</div>
                  {day.midshift.revised_priorities?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">{day.midshift.revised_priorities.map((p: string, i: number) => <span key={i} className="rounded bg-secondary px-2 py-0.5 text-xs">{p}</span>)}</div>
                  )}
                </Section>
              )}

              {day.closing && (
                <Section title={t("Closing Handoff")} icon={<DoorClosed size={16} className="text-primary" />}>
                  <Field label="Result" value={day.closing.result_summary} />
                  {day.closing.next_shift_priorities?.length > 0 && (
                    <div className="mt-2">
                      <div className="text-xs font-semibold uppercase text-muted-foreground">{t("Next shift must know")}</div>
                      <div className="mt-1 flex flex-wrap gap-1.5">{day.closing.next_shift_priorities.map((p: string, i: number) => <span key={i} className="rounded bg-accent px-2 py-0.5 text-xs text-accent-foreground">{p}</span>)}</div>
                    </div>
                  )}
                </Section>
              )}

              <Section title={t("Persistent Actions")} icon={<ListChecks size={16} className="text-primary" />} count={detail?.actions?.length}>
                <ItemList items={detail?.actions} render={(a: any) => (
                  <div className="flex items-start justify-between gap-2">
                    <div><div className="font-medium">{a.title}</div><div className="text-xs text-muted-foreground capitalize">{stat(a.priority)} · {a.source}</div></div>
                    <StatusPill status={a.status === "completed" ? "good" : a.status === "blocked" ? "critical" : "warn"}>{a.status.replace("_", " ")}</StatusPill>
                  </div>
                )} empty="No open actions" />
              </Section>
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          {history.length === 0 ? <EmptyState icon={<Radio size={26} />} title="No prior operating days" /> : (
            <Card className="overflow-hidden" data-testid="history-table">
              {history.map((d) => (
                <div key={d.id} className="flex items-center justify-between border-b border-border px-4 py-3 last:border-0">
                  <div>
                    <div className="font-medium">{d.business_date}</div>
                    <div className="text-xs text-muted-foreground">{t("MOD:")}{" "}{d.manager_on_duty}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    {d.closing?.result_summary && <span className="hidden max-w-xs truncate text-sm text-muted-foreground md:block">{d.closing.result_summary}</span>}
                    <StatusPill status={d.status === "active" ? "active" : "info"}>{stat(d.status)}</StatusPill>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </TabsContent>
        <TabsContent value="reviews">
          <ReviewsPanel storeId={storeId} />
        </TabsContent>
      </Tabs>
      {/* Dialogs */}
      {dayId && <>
        <EventDialog open={dialog === "capture"} onOpenChange={(v) => !v && setDialog(null)} title={t("Quick Capture")} endpoint={`/mc/store-days/${dayId}/capture`} onDone={() => loadDetail(dayId)} testId="dialog-capture"
          offlineCapture={{ storeDayId: dayId, onQueued: refreshPending }}
          fields={[{ name: "category", label: "Category", type: "select", required: true, options: [{value:"operations",label:"Operations"},{value:"customer",label:"Customer"},{value:"staff",label:"Staff"},{value:"facility",label:"Facility"},{value:"other",label:"Other"}] }, { name: "note", label: "Note", type: "textarea", required: true }]} />
        <EventDialog open={dialog === "condition"} onOpenChange={(v) => !v && setDialog(null)} title={t("Operating Condition")} endpoint={`/mc/store-days/${dayId}/conditions`} onDone={() => loadDetail(dayId)} testId="dialog-condition"
          fields={[{ name: "title", label: "Title", required: true }, { name: "severity", label: "Severity", type: "select", required: true, options: [{value:"low",label:"Low"},{value:"medium",label:"Medium"},{value:"high",label:"High"},{value:"critical",label:"Critical"}] }, { name: "business_impact", label: "Business impact" }, { name: "description", label: "Description", type: "textarea" }]} />
        <EventDialog open={dialog === "intervention"} onOpenChange={(v) => !v && setDialog(null)} title={t("Management Intervention")} endpoint={`/mc/store-days/${dayId}/interventions`} onDone={() => loadDetail(dayId)} testId="dialog-intervention"
          fields={[{ name: "action_taken", label: "Action taken", required: true }, { name: "rationale", label: "Rationale", type: "textarea" }, { name: "follow_up", label: "Follow-up" }]} />
        <EventDialog open={dialog === "missed"} onOpenChange={(v) => !v && setDialog(null)} title={t("Missed Demand")} endpoint={`/mc/store-days/${dayId}/missed-demand`} onDone={() => loadDetail(dayId)} testId="dialog-missed"
          fields={[{ name: "reason", label: "Reason", required: true }, { name: "estimated_units", label: "Estimated units", type: "number" }, { name: "description", label: "Description", type: "textarea" }]} />
        <EventDialog open={dialog === "kpi"} onOpenChange={(v) => !v && setDialog(null)} title={t("KPI Check")} endpoint={`/mc/store-days/${dayId}/kpi`} onDone={() => load()} testId="dialog-kpi"
          fields={[{ name: "metric", label: "Metric", required: true }, { name: "target", label: "Target", type: "number" }, { name: "actual", label: "Actual", type: "number" }, { name: "context_note", label: "Context", type: "textarea" }]} />
        <EventDialog open={dialog === "midshift"} onOpenChange={(v) => !v && setDialog(null)} title={t("Midshift Reset")} endpoint={`/mc/store-days/${dayId}/midshift`} onDone={() => load()} testId="dialog-midshift"
          fields={[{ name: "plan_vs_reality", label: "Plan vs. reality", type: "textarea", required: true }, { name: "revised_priorities", label: "Revised priorities", type: "list" }, { name: "deployment_changes", label: "Deployment changes" }]} />
        <EventDialog open={dialog === "close"} onOpenChange={(v) => !v && setDialog(null)} title={t("Closing Handoff")} endpoint={`/mc/store-days/${dayId}/close`} onDone={() => load()} testId="dialog-close"
          fields={[{ name: "result_summary", label: "Result summary", type: "textarea", required: true }, { name: "what_changed", label: "What changed" }, { name: "next_shift_priorities", label: "Next-shift priorities", type: "list" }]} />
      </>}
    </div>)
  );
}

const Field: React.FC<{ label: string; value?: any }> = ({ label, value }) => (  <div>
    <div className="text-xs font-semibold uppercase text-muted-foreground">{label}</div>
    <div className="mt-0.5">{value || "—"}</div>
  </div>
);

const AttendancePanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [state, setState] = useState<any>(null);
  useEffect(() => {
    api.get(`/mc/attendance?store_id=${storeId}`).then((r) => setState(r.data)).catch(() => setState({ available: false }));
  }, [storeId]);
  if (!state) return null;
  return (
    (<Card className="p-4" data-testid="mc-attendance-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-sm font-semibold"><Gauge size={16} className="text-brand" />{t("Attendance (live)")}</div>
        <Link to="/time" className="text-xs text-brand hover:underline" data-testid="mc-attendance-link">{t("Open Time & Attendance →")}</Link>
      </div>
      {!state.available ? (
        <div className="text-sm text-muted-foreground" data-testid="mc-attendance-unavailable">{state.reason || "Attendance context is unavailable."}</div>
      ) : (
        <div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs sm:grid-cols-6">
            {[["Scheduled", state.summary.scheduled], ["Present", state.summary.present], ["Absent", state.summary.absent], ["Late", state.summary.late], ["On clock", state.summary.on_clock], ["Exceptions", state.summary.exceptions]].map(([k, v]) => (
              <div key={k as string} className="rounded-md border border-border p-2"><div className="font-display text-lg font-bold">{v as number}</div><div className="text-muted-foreground">{k as string}</div></div>
            ))}
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">{t(
            "Read-only. Corrections, disputes and pay detail live in Time & Attendance."
          )}</div>
        </div>
      )}
    </Card>)
  );
};


const CoveragePanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [state, setState] = useState<any>(null);
  useEffect(() => {
    api.get(`/mc/coverage?store_id=${storeId}`).then((r) => setState(r.data)).catch(() => setState({ available: false }));
  }, [storeId]);
  if (!state) return null;
  return (
    (<Card className="p-4" data-testid="mc-coverage-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-sm font-semibold"><Gauge size={16} className="text-brand" />{t("Coverage (planned vs. actual)")}</div>
        <Link to="/workforce" className="text-xs text-brand hover:underline" data-testid="mc-coverage-link">{t("Open Workforce →")}</Link>
      </div>
      {!state.available ? (
        <div className="text-sm text-muted-foreground" data-testid="mc-coverage-unavailable">{state.reason || "Coverage context is unavailable."}</div>
      ) : (
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{t("Today ·")}{" "}{state.coverage.date}</span>
            <StatusPill status={state.coverage.coverage_pct >= 90 ? "good" : state.coverage.coverage_pct >= 70 ? "warn" : "critical"} testId="mc-coverage-pct">{state.coverage.coverage_pct}{t("% covered")}</StatusPill>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-2 text-center text-xs">
            {[["Planned", state.coverage.planned_shifts], ["Filled", state.coverage.filled_shifts], ["Open", state.coverage.open_shifts], ["Callouts", state.coverage.callouts]].map(([k, v]) => (
              <div key={k as string} className="rounded-md border border-border p-2"><div className="font-display text-lg font-bold">{v as number}</div><div className="text-muted-foreground">{k as string}</div></div>
            ))}
          </div>
          {state.coverage.coverage_gaps > 0 && (
            <div className="mt-2 text-[11px] text-status-warn" data-testid="mc-coverage-gaps">{state.coverage.coverage_gaps}{" "}{t("coverage gap(s) — manage in Workforce.")}</div>
          )}
        </div>
      )}
    </Card>)
  );
};

const PERF_STATE: Record<string, { label: string; cls: string; Icon: any }> = {
  on_track: { label: "On track", cls: "text-status-good", Icon: TrendingUp },
  watch: { label: "Watch", cls: "text-status-warn", Icon: AlertTriangle },
  off_target: { label: "Off target", cls: "text-status-critical", Icon: TrendingDown },
  stale: { label: "Stale", cls: "text-status-warn", Icon: History },
  missing_data: { label: "Missing data", cls: "text-muted-foreground", Icon: CircleDashed },
  not_configured: { label: "Not configured", cls: "text-muted-foreground", Icon: Minus },
  data_quality: { label: "Data-quality issue", cls: "text-status-warn", Icon: AlertTriangle },
};

function fmtVal(v: number | null, format?: string, unit?: string): string {
  if (v === null || v === undefined) return "—";
  if (format === "currency") return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);
  if (format === "percent" || unit === "%") return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(v)}%`;
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(v);
}

function fmtAsOf(iso?: string | null, tz?: string): string | null {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric", timeZone: tz || undefined }).format(new Date(iso));
  } catch { return null; }
}

const PerformancePanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [state, setState] = useState<any>(null);
  useEffect(() => {
    api.get(`/mc/performance?store_id=${storeId}`).then((r) => setState(r.data)).catch(() => setState({ available: false }));
  }, [storeId]);
  if (!state) return null;
  return (
    (<Card className="p-4" data-testid="mc-performance-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="flex items-center gap-2 font-display text-sm font-semibold"><BarChart3 size={16} className="text-brand" />{t("Today's Performance")}</div>
        <Link to="/intelligence" className="text-xs text-brand hover:underline" data-testid="mc-performance-link">{t("Open Performance & Intelligence →")}</Link>
      </div>
      {!state.available ? (
        <div className="text-sm text-muted-foreground" data-testid="mc-performance-unavailable">{state.reason || "Performance context is unavailable."}</div>
      ) : state.metrics.length === 0 ? (
        <div className="text-sm text-muted-foreground" data-testid="mc-performance-empty">{t("No priority metrics are configured for this tenant.")}</div>
      ) : (
        <div>
          {!state.period_is_today && (
            <div className="mb-2 flex items-center gap-1.5 text-[11px] text-status-warn" data-testid="mc-performance-stale">
              <History size={12} />{" "}{t("Latest available data ·")}{" "}{state.period_key}{" "}{t("(not today)")}</div>
          )}
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
            {state.metrics.map((m: any) => {
              const s = PERF_STATE[m.state] || PERF_STATE.not_configured;
              const asOf = fmtAsOf(m.as_of, state.iana_tz);
              const hasVar = m.variance_abs !== null && m.variance_abs !== undefined;
              return (
                (<div key={m.key} className="min-w-0 rounded-lg border border-border p-3" data-testid={`mc-perf-${m.key}`}>
                  <div className="flex items-start justify-between gap-1">
                    <span className="min-w-0 truncate text-xs font-medium leading-tight text-foreground">{m.name}</span>
                    <span className={`flex shrink-0 items-center gap-1 text-[10px] font-semibold ${s.cls}`} data-testid={`mc-perf-${m.key}-state`}>
                      <s.Icon size={12} /> {s.label}
                    </span>
                  </div>
                  <div className="mt-1.5 font-display text-xl font-bold" data-testid={`mc-perf-${m.key}-actual`}>{fmtVal(m.actual, m.format, m.unit)}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    {m.target !== null ? <>{t("Target")}{" "}{fmtVal(m.target, m.format, m.unit)}</> : t("No target configured")}
                    {hasVar && (
                      <span className={m.state === "off_target" ? t("text-status-critical") : ""}> · {m.variance_abs >= 0 ? "+" : ""}{fmtVal(m.variance_abs, m.format, m.unit)}{m.variance_pct !== null ? ` (${m.variance_pct >= 0 ? "+" : ""}${m.variance_pct}%)` : ""}</span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-1 text-[10px] text-muted-foreground">
                    <span data-testid={`mc-perf-${m.key}-prov`}>{m.provenance === "seeded_demo" ? t("Seeded demo") : (m.provenance || "—")}</span>
                    {asOf && <span className="whitespace-nowrap">{t("as of")}{" "}{asOf}</span>}
                  </div>
                </div>)
              );
            })}
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">{t(
            "Read-only. Full analysis, history and drill-down live in Performance & Intelligence."
          )}</div>
        </div>
      )}
    </Card>)
  );
};

const gfFmt = (v: number | null, metric: string) => v == null ? "—" : (metric === "sales" ? fmtMoney(v) : metric === "conversion" ? `${v}%` : fmtNum(Math.round(v)));

const GoalForecastPanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [data, setData] = useState<any>(null);
  useEffect(() => {
    api.get(`/goals/shift-summary?store_id=${storeId}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [storeId]);
  if (!data || data.error) return null;
  return (
    (<Card className="p-4" data-testid="goal-forecast-panel">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-sm font-semibold"><Target size={16} className="text-brand" />{t("Actual vs Forecast vs Goal")}</div>
        <span className="text-[11px] text-muted-foreground">{t("as of")}{" "}{fmtTime(data.as_of)}</span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {data.metrics.map((m: any) => (
          <div key={m.metric} className="rounded-md border border-border bg-muted/20 p-2.5" data-testid={`shift-goal-${m.metric}`}>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold capitalize">{m.metric}</span>
              {m.variance_to_goal && m.variance_to_goal.pct != null &&
                <span className={m.variance_to_goal.abs >= 0 ? t("text-[11px] text-status-good") : t("text-[11px] text-status-critical")}>{m.variance_to_goal.abs >= 0 ? "+" : ""}{m.variance_to_goal.pct}{t("% vs goal")}</span>}
            </div>
            <div className="grid grid-cols-3 gap-1 text-center">
              <div><div className="text-[10px] uppercase text-muted-foreground">{t("Actual")}</div><div className="text-sm font-semibold">{gfFmt(m.actual, m.metric)}</div></div>
              <div><div className="text-[10px] uppercase text-muted-foreground">{t("Forecast")}</div><div className="text-sm font-semibold text-brand">{gfFmt(m.forecast, m.metric)}</div></div>
              <div><div className="text-[10px] uppercase text-muted-foreground">{t("Goal")}</div><div className="text-sm font-semibold">{gfFmt(m.goal, m.metric)}</div></div>
            </div>
            <div className="mt-1 text-[10px] text-muted-foreground">{t("Finish ~")}{gfFmt(m.forecasted_finish, m.metric)}· {m.confidence}{" "}{t("confidence")}{m.forecast_status !== "ok" ? ` · ${m.forecast_status.replace(/_/g, " ")}` : ""}</div>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground">{t(
        "Actual = recorded · Forecast = system estimate · Goal = approved target. Details in Performance & Intelligence → Goals & Targets."
      )}</div>
    </Card>)
  );
};


const ItemList: React.FC<{ items?: any[]; render: (x: any) => React.ReactNode; empty: string }> = ({ items, render, empty }) => {
  if (!items || items.length === 0) return <div className="text-sm text-muted-foreground">{empty}</div>;
  return <div className="space-y-2.5">{items.map((it, i) => <div key={it.id || i} className="rounded-md border border-border bg-muted/20 p-2.5 text-sm">{render(it)}</div>)}</div>;
};

const OpenDayPanel: React.FC<{ canRun: boolean; onOpen: (mod: string, pr: string) => void }> = ({ canRun, onOpen }) => {
  const {
    t
  } = useI18n();

  const [mod, setMod] = useState("");
  const [pr, setPr] = useState("");
  if (!canRun) return <EmptyState icon={<Radio size={26} />} title="No active operating day" description="A store manager opens the operating day." />;
  return (
    (<Card className="max-w-lg p-6" data-testid="open-day-panel">
      <div className="mb-1 flex items-center gap-2 font-display text-lg font-semibold"><Radio size={18} className="text-brand" />{" "}{t("Open the operating day")}</div>
      <p className="mb-4 text-sm text-muted-foreground">{t(
        "Establish the plan for this shift. Only one operating day can be active per store."
      )}</p>
      <div className="space-y-3">
        <div><Label>{t("Manager on duty *")}</Label><Input className="mt-1" value={mod} onChange={(e) => setMod(e.target.value)} data-testid="open-mod" /></div>
        <div><Label>{t("Day priorities")}</Label><Input className="mt-1" value={pr} onChange={(e) => setPr(e.target.value)} placeholder={t("Restock, Coverage")} data-testid="open-priorities" /></div>
        <Button disabled={!mod} onClick={() => onOpen(mod, pr)} data-testid="open-day-submit"><PlayCircle size={16} className="mr-1.5" />{t("Open day")}</Button>
      </div>
    </Card>)
  );
};

const ReviewCard: React.FC<{ title: string; data: any; testId: string }> = ({ title, data, testId }) => {
  const {
    t
  } = useI18n();

  return (
    (<Card className="p-4" data-testid={testId}>
      <div className="mb-2 font-display font-semibold">{title}</div>
      {!data ? <div className="text-sm text-muted-foreground">{t("Loading…")}</div> : (
        <div className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">{t("Operating days")}</span><span className="font-medium">{data.days_count}{data.closed_days != null ? ` (${data.closed_days} closed)` : ""}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{t("Conditions")}</span><span>{data.event_counts?.condition ?? 0}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{t("Interventions")}</span><span>{data.event_counts?.intervention ?? 0}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{t("Missed demand events")}</span><span>{data.event_counts?.missed_demand ?? 0}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">{t("Quick captures")}</span><span>{data.event_counts?.capture ?? 0}</span></div>
          <div className="flex justify-between border-t border-border pt-2"><span className="text-muted-foreground">{t("Open actions")}</span><span className="font-medium">{data.open_actions}</span></div>
        </div>
      )}
      <div className="mt-3 text-[11px] text-muted-foreground">{t("Computed from stored records. No AI-generated summary.")}</div>
    </Card>)
  );
};

const ReviewsPanel: React.FC<{ storeId: string }> = ({ storeId }) => {
  const {
    t
  } = useI18n();

  const [weekly, setWeekly] = useState<any>(null);
  const [monthly, setMonthly] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    Promise.all([
      api.get(`/mc/reviews/weekly?store_id=${storeId}`),
      api.get(`/mc/reviews/monthly?store_id=${storeId}`),
    ]).then(([w, m]) => { setWeekly(w.data); setMonthly(m.data); })
      .catch((e) => setErr(e?.response?.data?.detail || "Reviews unavailable"));
  }, [storeId]);
  if (err) return <EmptyState title="Reviews unavailable" description={err} />;
  return (
    (<div className="grid gap-4 sm:grid-cols-2">
      <ReviewCard title={t("Weekly Review (last 7 days)")} data={weekly} testId="weekly-review" />
      <ReviewCard title={t("Monthly Review (last 30 days)")} data={monthly} testId="monthly-review" />
    </div>)
  );
};
